/**
 * RECIP 1.0 PSMA-VOL:
 * 
 *   Threshold, per CT-PET pair:
 *     bone:        SUV > 3.0 
 *     soft tissue: SUV > 4.30 / liverMean * (liverMean + liverSD)
 * 
 */

import { ROI_PARTIAL_VOLUME_THRESHOLD } from './suvSampleCache';

export const PSMA_METHOD_VERSION = 'psma-vol-v1';
export const BONE_SUV_THRESHOLD = 3.0;
export const SOFT_TISSUE_COEFFICIENT = 4.3;
export const REF_LIVER_LOCATION = 'REF-LIVER';

const isReferenceLink = link =>
  link.type === 'BACKGROUND' || !!link.location?.startsWith('REF-');

export function findRefLiverROI(rois = [], links = []) {
  const matched = rois.filter(roi => {
    const link = links.find(l => l.link_id === roi.link_id);
    return link?.location === REF_LIVER_LOCATION;
  });
  if (matched.length === 0) return { roi: null, reason: 'no-ref-liver' };
  if (matched.length > 1) return { roi: null, reason: 'multiple-ref-liver' }; // Exactly one is required.
  return { roi: matched[0], reason: null };
}

export function computePsmaThresholds(liverStats, refReason = null) {
  const base = {
    bone: BONE_SUV_THRESHOLD,
    softTissue: null,
    softTissueReason: null,
    liverMean: null,
    liverSD: null,
    methodVersion: PSMA_METHOD_VERSION,
  };
  if (refReason) return { ...base, softTissueReason: refReason };
  if (!liverStats || liverStats.ne) {
    return { ...base, softTissueReason: 'ref-liver-ne' };
  }
  const { suvMean, suvSD } = liverStats;
  if (!(suvMean > 0) || !Number.isFinite(suvSD)) {
    return { ...base, softTissueReason: 'ref-liver-invalid' };
  }
  return {
    ...base,
    softTissue: (SOFT_TISSUE_COEFFICIENT / suvMean) * (suvMean + suvSD),
    liverMean: suvMean,
    liverSD: suvSD,
  };
}

export function computePsmaVolume(samples, thresholds) {
  if (!samples || !thresholds) return null;
  const { suv, frac, petVoxelVolumeMm3 } = samples;
  if (!suv || !frac || !(petVoxelVolumeMm3 > 0)) return null;

  const hasSoftTissue = thresholds.softTissue != null;
  let boneCount = 0;
  let softTissueCount = 0;
  for (let i = 0; i < suv.length; i++) {
    if (frac[i] < ROI_PARTIAL_VOLUME_THRESHOLD) continue;
    if (suv[i] > thresholds.bone) boneCount += 1;
    if (hasSoftTissue && suv[i] > thresholds.softTissue) softTissueCount += 1;
  }
  return {
    bone_thres: thresholds.bone,
    bone_vol: boneCount * petVoxelVolumeMm3,
    soft_tissue_thres: hasSoftTissue ? thresholds.softTissue : null,
    soft_tissue_vol: hasSoftTissue ? softTissueCount * petVoxelVolumeMm3 : null,
  };
}

export function selectPsmaVol(psmaVolume, link) {
  if (!psmaVolume || !link?.link_id || isReferenceLink(link)) return null;
  return link.location === 'BONE'
    ? psmaVolume.bone_vol
    : psmaVolume.soft_tissue_vol;
}

export function sumPsmaVol(values = []) {
  if (values.some((v) => v == null)) return null;
  return values.reduce((acc, v) => acc + v, 0);
}